/**
 * Full-screen 3D VS intro: two portrait plates slam over a gold arena.
 */

import * as THREE from 'three'
import { RoundedBoxGeometry } from 'three/examples/jsm/geometries/RoundedBoxGeometry.js'
import type { AvatarConfig } from '@shared/index'
import { composeAvatar } from '../../avatar/composeAvatar'
import {
  applyWebglBudget,
  listenWebglLost,
  webglRendererOptions,
} from '../webglPhone'
import './VsArena.css'

export interface VsArenaOpts {
  left: AvatarConfig
  right: AvatarConfig
  onImpact?: () => void
  /** The GPU dropped the canvas. The overlay should show the flat portraits. */
  onContextLost?: () => void
}

interface CardRig {
  group: THREE.Group
  glow: THREE.Mesh
  glowMat: THREE.MeshBasicMaterial
  light: THREE.PointLight
  side: -1 | 1
}

interface Spark {
  sprite: THREE.Sprite
  dir: THREE.Vector3
  speed: number
  life: number
  born: number
}

/** Square framed portrait, same crop as Profile. */
const PLATE_W = 2.28
const PLATE_H = 2.28
const PLATE_D = 0.04
const PLATE_REST_Y = PLATE_H / 2 + 0.04
const ART_TEX_W = 1024
const IMPACT_AT = 0.88
const SHAKE_MS = 0.32

const clamp01 = (t: number): number => Math.max(0, Math.min(1, t))
const lerp = (a: number, b: number, t: number): number => a + (b - a) * t
const easeOutQuart = (t: number): number => 1 - (1 - t) ** 4
const SPARK_COLORS = [0xffffff, 0xfde68a, 0xfbbf24] as const

const hash01 = (i: number): number => {
  const x = Math.sin(i * 127.1 + 311.7) * 43758.5453
  return x - Math.floor(x)
}

function prefersReducedMotion(): boolean {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

/**
 * Full-screen 3D VS intro: two portrait plates slam over a gold arena.
 */
export class VsArena {
  private readonly root: HTMLDivElement
  private readonly canvas: HTMLCanvasElement
  private readonly renderer: THREE.WebGLRenderer
  private readonly scene: THREE.Scene
  private readonly camera: THREE.PerspectiveCamera
  private readonly disposables: { dispose: () => void }[] = []
  private readonly opts: VsArenaOpts
  private readonly reduced: boolean
  private readonly resizeObs: ResizeObserver
  private readonly dummy = new THREE.Object3D()
  private readonly left: CardRig
  private readonly right: CardRig
  private floor!: THREE.Mesh
  private ring!: THREE.Mesh
  private ring2!: THREE.Mesh
  private beam!: THREE.Mesh
  private flash!: THREE.Mesh
  private dust!: THREE.Points
  private readonly sparks: Spark[] = []
  private sparkTex: THREE.CanvasTexture | null = null
  private facesStarted = false
  private raf = 0
  private unlistenGl: (() => void) | null = null
  private contextLost = false
  private disposed = false
  private impactSent = false
  private startedAt = 0

  constructor(host: HTMLElement, opts: VsArenaOpts) {
    this.opts = opts
    this.reduced = prefersReducedMotion()

    this.root = document.createElement('div')
    this.root.className = 'vs-arena-root'
    this.canvas = document.createElement('canvas')
    this.canvas.className = 'vs-arena-canvas'
    this.root.append(this.canvas)

    try {
      this.renderer = new THREE.WebGLRenderer(webglRendererOptions(this.canvas))
    } catch (err) {
      this.canvas.remove()
      throw err
    }
    applyWebglBudget(this.renderer)
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping
    this.renderer.toneMappingExposure = 1.12
    this.renderer.setClearColor(0x07080e, 0)
    if (this.renderer.shadowMap.enabled) {
      this.renderer.shadowMap.type = THREE.PCFShadowMap
    }
    this.unlistenGl = listenWebglLost(this.canvas, this.handleContextLost)

    this.scene = new THREE.Scene()
    this.scene.fog = new THREE.FogExp2(0x07080e, 0.038)
    this.camera = new THREE.PerspectiveCamera(36, 1, 0.1, 40)

    this.addLights()
    this.addArena()
    this.addDust()
    this.addFx()

    this.left = this.makeCard(-1, 0x5eead4)
    this.right = this.makeCard(1, 0xf87171)
    this.scene.add(this.left.group, this.right.group)
    this.poseCards(this.reduced ? IMPACT_AT + 1 : 0)

    this.startedAt = performance.now()
    this.resizeObs = new ResizeObserver(() => this.resize())
    try {
      host.appendChild(this.root)
      this.resizeObs.observe(this.root)
      this.resize()
      this.loop()
    } catch (err) {
      this.unlistenGl()
      this.unlistenGl = null
      this.resizeObs.disconnect()
      this.root.remove()
      throw err
    }
  }

  private handleContextLost = (): void => {
    if (this.disposed || this.contextLost) return
    this.contextLost = true
    if (this.raf) cancelAnimationFrame(this.raf)
    this.raf = 0
    this.opts.onContextLost?.()
  }

  dispose(): void {
    this.disposed = true
    cancelAnimationFrame(this.raf)
    this.unlistenGl?.()
    this.unlistenGl = null
    this.resizeObs.disconnect()
    for (const item of this.disposables) item.dispose()
    try {
      this.renderer.forceContextLoss()
    } catch {
      /* Already lost. */
    }
    this.renderer.dispose()
    this.root.remove()
  }

  private track<T extends { dispose: () => void }>(item: T): T {
    this.disposables.push(item)
    return item
  }

  private layout(): { restX: number; scale: number; camZ: number } {
    const aspect = Math.max(0.4, this.camera.aspect)
    let scale: number
    let camZ: number
    if (aspect < 0.78) {
      scale = 0.58
      camZ = 9.8
    } else if (aspect < 1.05) {
      scale = 0.72
      camZ = 8.4
    } else if (aspect < 1.55) {
      scale = 0.8
      camZ = 7.6
    } else {
      scale = 0.88
      camZ = 7.1
    }
    const halfH = Math.tan((34 * Math.PI) / 360) * camZ
    const halfW = halfH * aspect
    const plateHalf = (PLATE_W * scale) / 2
    const margin = 0.34
    const restX = Math.max(
      plateHalf + 0.16,
      Math.min(2.05, halfW - plateHalf - margin),
    )
    return { restX, scale, camZ }
  }

  private addLights(): void {
    const ambient = new THREE.AmbientLight(0x9aa4c2, 0.28)
    const hemi = new THREE.HemisphereLight(0xffe7b8, 0x121826, 0.55)
    const key = new THREE.DirectionalLight(0xfff4e0, 1.35)
    key.position.set(0.4, 6.4, 4.8)
    key.castShadow = true
    key.shadow.mapSize.set(1024, 1024)
    key.shadow.camera.near = 0.5
    key.shadow.camera.far = 18
    key.shadow.camera.left = -6
    key.shadow.camera.right = 6
    key.shadow.camera.top = 5
    key.shadow.camera.bottom = -4
    const fillL = new THREE.PointLight(0x5eead4, 1.15, 14)
    fillL.position.set(-3.6, 2.2, 2.4)
    const fillR = new THREE.PointLight(0xf87171, 1.15, 14)
    fillR.position.set(3.6, 2.2, 2.4)
    const rim = new THREE.PointLight(0xfde68a, 0.7, 12)
    rim.position.set(0, 1.6, -3.4)
    this.scene.add(ambient, hemi, key, fillL, fillR, rim)
  }

  private addArena(): void {
    const floorTex = this.makeFloorTex()
    const floorMat = this.track(
      new THREE.MeshStandardMaterial({
        map: floorTex,
        roughness: 0.42,
        metalness: 0.55,
        emissive: new THREE.Color(0x3a2e10),
        emissiveIntensity: 0.22,
      }),
    )
    const floor = new THREE.Mesh(
      this.track(new THREE.CircleGeometry(5.4, 64)),
      floorMat,
    )
    floor.rotation.x = -Math.PI / 2
    floor.receiveShadow = true
    this.floor = floor
    this.scene.add(floor)

    const shadow = new THREE.Mesh(
      this.track(new THREE.CircleGeometry(5.2, 40)),
      this.track(new THREE.ShadowMaterial({ opacity: 0.42 })),
    )
    shadow.rotation.x = -Math.PI / 2
    shadow.position.y = 0.01
    shadow.receiveShadow = true
    this.scene.add(shadow)

    const gold = this.track(
      new THREE.MeshStandardMaterial({
        color: 0xd4af37,
        roughness: 0.28,
        metalness: 0.82,
        emissive: new THREE.Color(0x6a4a08),
        emissiveIntensity: 0.18,
      }),
    )
    const lip = new THREE.Mesh(
      this.track(new THREE.TorusGeometry(4.55, 0.055, 10, 80)),
      gold,
    )
    lip.rotation.x = Math.PI / 2
    lip.position.y = 0.04
    lip.castShadow = true
    this.scene.add(lip)

    const inner = new THREE.Mesh(
      this.track(new THREE.TorusGeometry(2.35, 0.028, 8, 64)),
      gold,
    )
    inner.rotation.x = Math.PI / 2
    inner.position.y = 0.03
    this.scene.add(inner)

    const wall = new THREE.Mesh(
      this.track(new THREE.CylinderGeometry(7.6, 7.6, 4.6, 48, 1, true)),
      this.track(
        new THREE.MeshStandardMaterial({
          color: 0x0b0e16,
          roughness: 0.92,
          metalness: 0.18,
          side: THREE.BackSide,
        }),
      ),
    )
    wall.position.y = 1.4
    this.scene.add(wall)
  }

  private addDust(): void {
    const count = 140
    const pos = new Float32Array(count * 3)
    for (let i = 0; i < count; i++) {
      pos[i * 3] = (hash01(i) - 0.5) * 11
      pos[i * 3 + 1] = hash01(i + 17) * 4.2
      pos[i * 3 + 2] = (hash01(i + 41) - 0.5) * 8
    }
    const geo = this.track(new THREE.BufferGeometry())
    geo.setAttribute('position', new THREE.BufferAttribute(pos, 3))
    const mat = this.track(
      new THREE.PointsMaterial({
        size: 0.045,
        color: 0xfde68a,
        transparent: true,
        opacity: 0.42,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
      }),
    )
    this.dust = new THREE.Points(geo, mat)
    this.scene.add(this.dust)
  }

  private addFx(): void {
    const ringMat = this.track(
      new THREE.MeshBasicMaterial({
        color: 0xfde68a,
        transparent: true,
        opacity: 0,
        side: THREE.DoubleSide,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
      }),
    )
    this.ring = new THREE.Mesh(
      this.track(new THREE.RingGeometry(0.35, 0.52, 48)),
      ringMat,
    )
    this.ring.rotation.x = -Math.PI / 2
    this.ring.position.y = 0.12
    this.ring2 = new THREE.Mesh(
      this.track(new THREE.RingGeometry(0.22, 0.34, 48)),
      ringMat.clone(),
    )
    this.track(this.ring2.material as THREE.Material)
    this.ring2.rotation.x = -Math.PI / 2
    this.ring2.position.y = 0.14
    this.scene.add(this.ring, this.ring2)

    const beamMat = this.track(
      new THREE.MeshBasicMaterial({
        color: 0xfff6c8,
        transparent: true,
        opacity: 0,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
      }),
    )
    this.beam = new THREE.Mesh(
      this.track(new THREE.CylinderGeometry(0.025, 0.025, 4.2, 10)),
      beamMat,
    )
    this.beam.rotation.z = Math.PI / 2
    this.beam.position.y = PLATE_REST_Y
    this.scene.add(this.beam)

    const flashMat = this.track(
      new THREE.MeshBasicMaterial({
        color: 0xfff7e0,
        transparent: true,
        opacity: 0,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
        side: THREE.DoubleSide,
      }),
    )
    this.flash = new THREE.Mesh(
      this.track(new THREE.PlaneGeometry(16, 10)),
      flashMat,
    )
    this.flash.position.set(0, PLATE_REST_Y, 2.4)
    this.scene.add(this.flash)

    this.sparkTex = this.makeSparkTex()
    for (let i = 0; i < 42; i++) {
      const mat = this.track(
        new THREE.SpriteMaterial({
          map: this.sparkTex,
          color: SPARK_COLORS[i % SPARK_COLORS.length] ?? 0xfde68a,
          transparent: true,
          opacity: 0,
          blending: THREE.AdditiveBlending,
          depthWrite: false,
        }),
      )
      const sprite = new THREE.Sprite(mat)
      sprite.visible = false
      sprite.scale.setScalar(0.08)
      this.scene.add(sprite)
      const a = (i / 42) * Math.PI * 2
      this.sparks.push({
        sprite,
        dir: new THREE.Vector3(
          Math.cos(a) * (0.7 + (i % 5) * 0.12),
          0.35 + (i % 7) * 0.11,
          Math.sin(a) * 0.55,
        ),
        speed: 1.6 + (i % 6) * 0.18,
        life: 0.42 + (i % 5) * 0.06,
        born: -1,
      })
    }
  }

  private makeCard(side: -1 | 1, glowColor: number): CardRig {
    const group = new THREE.Group()
    const gold = this.track(
      new THREE.MeshStandardMaterial({
        color: 0xd4af37,
        roughness: 0.3,
        metalness: 0.78,
        emissive: new THREE.Color(0x5a4308),
        emissiveIntensity: 0.16,
      }),
    )
    const coreGeo = new RoundedBoxGeometry(
      PLATE_W + 0.05,
      PLATE_H + 0.05,
      PLATE_D,
      3,
      0.014,
    )
    const core = new THREE.Mesh(this.track(coreGeo), gold)
    core.castShadow = true
    core.receiveShadow = true
    group.add(core)

    const placeholder = this.makePlaceholder(side < 0)
    const frontMat = this.track(
      new THREE.MeshStandardMaterial({
        map: placeholder,
        emissive: new THREE.Color(0xffffff),
        emissiveMap: placeholder,
        emissiveIntensity: 0.32,
        roughness: 0.38,
        metalness: 0.08,
      }),
    )
    const front = new THREE.Mesh(
      this.track(new THREE.PlaneGeometry(PLATE_W * 0.955, PLATE_H * 0.955)),
      frontMat,
    )
    front.position.z = PLATE_D / 2 + 0.001
    front.name = 'front'
    group.add(front)

    const backMat = this.track(
      new THREE.MeshStandardMaterial({
        color: 0x1a1408,
        roughness: 0.38,
        metalness: 0.62,
        emissive: new THREE.Color(0x3a2a08),
        emissiveIntensity: 0.12,
      }),
    )
    const back = new THREE.Mesh(
      this.track(new THREE.PlaneGeometry(PLATE_W * 0.955, PLATE_H * 0.955)),
      backMat,
    )
    back.position.z = -(PLATE_D / 2 + 0.001)
    back.rotation.y = Math.PI
    group.add(back)

    const glowMat = this.track(
      new THREE.MeshBasicMaterial({
        color: glowColor,
        transparent: true,
        opacity: 0.22,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
        side: THREE.DoubleSide,
      }),
    )
    const glow = new THREE.Mesh(
      this.track(new THREE.PlaneGeometry(PLATE_W * 1.45, PLATE_H * 1.45)),
      glowMat,
    )
    glow.position.z = -0.08
    group.add(glow)

    const light = new THREE.PointLight(glowColor, 0.55, 4.5)
    light.position.set(0, 0, 0.8)
    group.add(light)

    group.scale.setScalar(0.001)
    return { group, glow, glowMat, light, side }
  }

  private async loadFaces(): Promise<void> {
    const [leftCanvas, rightCanvas] = await Promise.all([
      composeAvatar(this.opts.left, { width: ART_TEX_W }),
      composeAvatar(this.opts.right, { width: ART_TEX_W }),
    ])
    if (this.disposed) return
    this.applyFace(this.left, this.texFromCanvas(leftCanvas))
    this.applyFace(this.right, this.texFromCanvas(rightCanvas))
  }

  private applyFace(rig: CardRig, front: THREE.Texture): void {
    const face = rig.group.getObjectByName('front') as THREE.Mesh | undefined
    if (!face) return
    const mat = face.material as THREE.MeshStandardMaterial
    mat.map = front
    mat.emissiveMap = front
    mat.needsUpdate = true
  }

  private texFromCanvas(canvas: HTMLCanvasElement): THREE.Texture {
    const tex = new THREE.CanvasTexture(canvas)
    tex.colorSpace = THREE.SRGBColorSpace
    tex.anisotropy = this.renderer.capabilities.getMaxAnisotropy()
    tex.needsUpdate = true
    return this.track(tex)
  }

  private makePlaceholder(blue: boolean): THREE.CanvasTexture {
    const w = 512
    const h = 512
    const c = document.createElement('canvas')
    c.width = w
    c.height = h
    const ctx = c.getContext('2d')
    if (ctx) {
      const g = ctx.createLinearGradient(0, 0, 0, h)
      g.addColorStop(0, blue ? '#1e3a5f' : '#7f1d1d')
      g.addColorStop(1, '#111827')
      ctx.fillStyle = g
      ctx.fillRect(0, 0, w, h)
      ctx.strokeStyle = '#e8c547'
      ctx.lineWidth = 8
      ctx.strokeRect(8, 8, w - 16, h - 16)
    }
    const tex = new THREE.CanvasTexture(c)
    tex.colorSpace = THREE.SRGBColorSpace
    return this.track(tex)
  }

  private makeFloorTex(): THREE.CanvasTexture {
    const size = 1024
    const c = document.createElement('canvas')
    c.width = c.height = size
    const ctx = c.getContext('2d')
    if (ctx) {
      ctx.fillStyle = '#080a12'
      ctx.fillRect(0, 0, size, size)
      const rg = ctx.createRadialGradient(512, 512, 20, 512, 512, 500)
      rg.addColorStop(0, 'rgba(232,197,71,0.22)')
      rg.addColorStop(0.35, 'rgba(30,58,95,0.16)')
      rg.addColorStop(0.7, 'rgba(127,29,29,0.1)')
      rg.addColorStop(1, 'rgba(0,0,0,0)')
      ctx.fillStyle = rg
      ctx.fillRect(0, 0, size, size)
      for (let i = 0; i < 48; i++) {
        const a = (i / 48) * Math.PI * 2
        ctx.beginPath()
        ctx.moveTo(512 + Math.cos(a) * 90, 512 + Math.sin(a) * 90)
        ctx.lineTo(512 + Math.cos(a) * 470, 512 + Math.sin(a) * 470)
        ctx.strokeStyle =
          i % 8 === 0 ? 'rgba(232,197,71,0.42)' : 'rgba(232,197,71,0.1)'
        ctx.lineWidth = i % 8 === 0 ? 3 : 1
        ctx.stroke()
      }
      ctx.strokeStyle = 'rgba(232,197,71,0.55)'
      ctx.lineWidth = 8
      ctx.beginPath()
      ctx.arc(512, 512, 455, 0, Math.PI * 2)
      ctx.stroke()
      ctx.lineWidth = 3
      ctx.beginPath()
      ctx.arc(512, 512, 240, 0, Math.PI * 2)
      ctx.stroke()
    }
    const tex = new THREE.CanvasTexture(c)
    tex.colorSpace = THREE.SRGBColorSpace
    tex.anisotropy = 4
    return this.track(tex)
  }

  private makeSparkTex(): THREE.CanvasTexture {
    const c = document.createElement('canvas')
    c.width = c.height = 64
    const ctx = c.getContext('2d')
    if (ctx) {
      const g = ctx.createRadialGradient(32, 32, 0, 32, 32, 32)
      g.addColorStop(0, 'rgba(255,255,255,1)')
      g.addColorStop(0.2, 'rgba(253,224,71,0.95)')
      g.addColorStop(0.55, 'rgba(250,204,21,0.3)')
      g.addColorStop(1, 'rgba(250,204,21,0)')
      ctx.fillStyle = g
      ctx.fillRect(0, 0, 64, 64)
    }
    const tex = new THREE.CanvasTexture(c)
    tex.colorSpace = THREE.SRGBColorSpace
    return this.track(tex)
  }

  private poseCards(t: number): void {
    const { restX, scale, camZ } = this.layout()
    const slamT = this.reduced ? 1 : easeOutQuart(clamp01(t / IMPACT_AT))
    this.poseOne(this.left, slamT, t, restX, scale)
    this.poseOne(this.right, slamT, t, restX, scale)
    this.poseCamera(t, slamT, camZ)
    this.poseFx(t)
  }

  private poseOne(
    rig: CardRig,
    slamT: number,
    t: number,
    restX: number,
    scale: number,
  ): void {
    const side = rig.side
    const restY = (PLATE_H * scale) / 2 + 0.04
    const fromX = side * 7.6
    const fromY = 2.15
    const fromZ = -1.8
    let x = lerp(fromX, side * restX, slamT)
    let y = lerp(fromY, restY, slamT)
    let z = lerp(fromZ, 0.05, slamT)
    const hold = Math.max(0, t - IMPACT_AT)
    if (t >= IMPACT_AT) {
      const punch = Math.sin(clamp01(hold / 0.2) * Math.PI) * 0.1
      x += side * punch
      y += punch * 0.35
      y += Math.sin(hold * 1.7) * 0.045
      z += Math.sin(hold * 1.1 + side) * 0.03
    }
    rig.group.position.set(x, y, z)

    const fromYaw = side * Math.PI * 2.15
    const restYaw = side * 0.36
    const yaw = lerp(fromYaw, restYaw, slamT)
    const pitch = lerp(0.55, -0.05, slamT)
    const roll = lerp(side * 0.55, side * -0.04, slamT)
    const idleYaw = t >= IMPACT_AT ? Math.sin(hold * 0.9) * 0.05 * side : 0
    rig.group.rotation.set(pitch, yaw + idleYaw, roll)

    const pop = t >= IMPACT_AT
      ? 1 + Math.sin(clamp01(hold / 0.18) * Math.PI) * 0.07
      : lerp(0.55, 1, slamT)
    rig.group.scale.setScalar(scale * (this.reduced ? 1 : pop))

    const glowBoost = t >= IMPACT_AT
      ? 0.38 + Math.exp(-hold * 2.4) * 0.55
      : 0.12 + slamT * 0.2
    rig.glowMat.opacity = glowBoost
    rig.light.intensity = 0.4 + glowBoost * 1.4
  }

  private poseCamera(t: number, slamT: number, camZ: number): void {
    const hold = Math.max(0, t - IMPACT_AT)
    let x = 0
    let y = lerp(2.15, PLATE_REST_Y + 0.32, slamT)
    let z = lerp(9.6, camZ, slamT)
    if (t >= IMPACT_AT) {
      y += Math.sin(hold * 0.7) * 0.06
      z += hold * 0.05
      x = Math.sin(hold * 0.28) * 0.22
      if (hold < SHAKE_MS && !this.reduced) {
        const s = 1 - hold / SHAKE_MS
        x += (hash01(Math.floor(t * 60)) - 0.5) * 0.16 * s
        y += (hash01(Math.floor(t * 60) + 9) - 0.5) * 0.1 * s
      }
    }
    this.camera.position.set(x, y, z)
    this.dummy.position.set(0, PLATE_REST_Y, 0)
    this.camera.lookAt(this.dummy.position)
    this.camera.fov = lerp(40, 34, slamT)
    this.camera.updateProjectionMatrix()
  }

  private poseFx(t: number): void {
    this.floor.rotation.z = t * 0.08
    this.dust.rotation.y = t * 0.05
    const hold = t - IMPACT_AT
    this.poseImpactMeshes(hold)
    this.poseSparks(t, hold)
  }

  private poseImpactMeshes(hold: number): void {
    const live = hold >= 0
    const fade = live ? Math.exp(-hold * 2.8) : 0
    this.ring.scale.setScalar(live ? 1 + hold * 9 : 0.2)
    const ringMat = this.ring.material as THREE.MeshBasicMaterial
    ringMat.opacity = live ? fade * 0.85 : 0
    const h2 = hold - 0.06
    const live2 = h2 >= 0
    this.ring2.scale.setScalar(live2 ? 1 + h2 * 11 : 0.2)
    const ring2Mat = this.ring2.material as THREE.MeshBasicMaterial
    ring2Mat.opacity = live2 ? Math.exp(-h2 * 3.1) * 0.7 : 0
    const beamMat = this.beam.material as THREE.MeshBasicMaterial
    beamMat.opacity = live ? fade * 0.9 : 0
    this.beam.scale.set(1, live ? 1 + (1 - fade) * 0.2 : 0.2, 1)
    const flashMat = this.flash.material as THREE.MeshBasicMaterial
    flashMat.opacity = live && hold < 0.22 ? (1 - hold / 0.22) * 0.28 : 0
  }

  private poseSparks(t: number, hold: number): void {
    const live = hold >= 0
    const first = this.sparks[0]
    if (live && first && first.born < 0) {
      for (const spark of this.sparks) {
        spark.born = t
        spark.sprite.visible = true
        spark.sprite.position.set(0, PLATE_REST_Y, 0.2)
      }
    }
    for (const spark of this.sparks) {
      if (spark.born < 0) continue
      const age = t - spark.born
      const u = clamp01(age / spark.life)
      spark.sprite.position.set(
        spark.dir.x * spark.speed * age,
        PLATE_REST_Y + spark.dir.y * spark.speed * age - age * age * 1.4,
        spark.dir.z * spark.speed * age,
      )
      const mat = spark.sprite.material as THREE.SpriteMaterial
      const fadeS = u < 0.18 ? u / 0.18 : 1 - (u - 0.18) / 0.82
      mat.opacity = fadeS * 0.85
      spark.sprite.scale.setScalar(0.06 + fadeS * 0.16)
      spark.sprite.visible = u < 1
    }
  }

  private fireImpact(): void {
    if (this.impactSent) return
    this.impactSent = true
    this.opts.onImpact?.()
  }

  private resize(): void {
    const w = Math.max(1, this.root.clientWidth)
    const h = Math.max(1, this.root.clientHeight)
    this.camera.aspect = w / h
    this.camera.updateProjectionMatrix()
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2))
    this.renderer.setSize(w, h, false)
  }

  private loop = (): void => {
    if (this.disposed || this.contextLost) return
    this.raf = requestAnimationFrame(this.loop)
    if (!this.facesStarted) {
      this.facesStarted = true
      void this.loadFaces()
    }
    const t = this.reduced
      ? IMPACT_AT + 1
      : (performance.now() - this.startedAt) / 1000
    if (t >= IMPACT_AT) this.fireImpact()
    this.poseCards(t)
    this.renderer.render(this.scene, this.camera)
  }
}
